import type { CardPreset, DeckProject, ElementType, ProjectAsset, VariableType, VisualElement } from './types'
import { newId } from './utils'
import { createDefaultGradient, createDefaultShadow, gradientStopBindingKey } from './visual-style'

export const CARD_PRESETS: CardPreset[] = [
  {
    id: 'poker-bgm',
    name: 'Poker',
    detail: '63.5 × 88.9 mm',
    widthMm: 63.5,
    heightMm: 88.9,
    recommendedBleedMm: 3.05,
    safeMm: 3.18,
    cornerRadiusMm: 3,
  },
  {
    id: 'bridge',
    name: 'Bridge',
    detail: '57.15 × 88.9 mm',
    widthMm: 57.15,
    heightMm: 88.9,
    recommendedBleedMm: 3.18,
    safeMm: 3.18,
    cornerRadiusMm: 3,
  },
  {
    id: 'tarot',
    name: 'Tarot',
    detail: '69.85 × 120.65 mm',
    widthMm: 69.85,
    heightMm: 120.65,
    recommendedBleedMm: 3.18,
    safeMm: 3.18,
    cornerRadiusMm: 3,
  },
  {
    id: 'mini',
    name: 'Mini',
    detail: '44.45 × 63.5 mm',
    widthMm: 44.45,
    heightMm: 63.5,
    recommendedBleedMm: 3.18,
    safeMm: 3.18,
    cornerRadiusMm: 2.5,
  },
  {
    id: 'square',
    name: 'Square',
    detail: '70 × 70 mm',
    widthMm: 70,
    heightMm: 70,
    recommendedBleedMm: 3,
    safeMm: 3,
    cornerRadiusMm: 3,
  },
]

export const CUSTOM_CARD_PRESET: CardPreset = {
  id: 'custom',
  name: 'Custom',
  detail: 'Set your own trim size',
  widthMm: 63.5,
  heightMm: 88.9,
  recommendedBleedMm: 3,
  safeMm: 3,
  cornerRadiusMm: 3,
}

export interface CustomCardSize {
  widthMm: number
  heightMm: number
}

export type StarterProjectId = 'empty' | 'cute' | 'spooky'

export const STARTER_PROJECTS: { id: StarterProjectId; name: string; detail: string }[] = [
  { id: 'empty', name: 'Empty', detail: 'A blank template with one empty card.' },
  { id: 'cute', name: 'Cute', detail: 'An illustrated three-card deck adapted to every format.' },
  { id: 'spooky', name: 'Spooky', detail: 'A dark, sharp-edged three-card deck with eerie sample artwork.' },
]

export const defaultVariableWidth = (type: VariableType) => {
  if (type === 'number') return 110
  if (type === 'color') return 145
  if (type === 'boolean') return 90
  if (type === 'image') return 150
  return 230
}

const baseElement = (type: ElementType, name: string): VisualElement => {
  const id = newId('element')
  return {
    id,
    type,
    name,
    x: 8,
    y: 8,
    width: 30,
    height: 18,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    fillMode: type === 'text' || type === 'line' ? 'none' : 'solid',
    fill: '#E66B3C',
    gradient: createDefaultGradient(`${id}-gradient-stop`),
    shadow: createDefaultShadow(),
    stroke: '#1B1D18',
    strokeWidth: 0,
    strokeStyle: 'solid',
    strokeDashLength: 2,
    strokeDashGap: 1,
    lineCap: 'round',
    lineJoin: 'round',
    startArrow: false,
    endArrow: false,
    cornerRadius: 2,
    polygonSides: 6,
    starPoints: 5,
    starInnerRatio: 0.48,
    arcStartAngle: -90,
    arcSweepAngle: 120,
    ringInnerRatio: 0.7,
    text: 'Text',
    fontSize: 5,
    fontFamily: 'Arial',
    fontWeight: 700,
    fontStyle: 'normal',
    letterSpacing: 0,
    lineHeight: 1.22,
    textDecoration: 'none',
    textTransform: 'none',
    align: 'left',
    verticalAlign: 'top',
    imageFit: 'cover',
    imageScale: 1,
    imageOffsetX: 0,
    imageOffsetY: 0,
    imageRotation: 0,
    imageOpacity: 1,
    bindings: {},
  }
}

export const createElement = (type: ElementType, index = 0): VisualElement => {
  const offset = index * 2
  switch (type) {
    case 'text':
      return {
        ...baseElement(type, 'Text'),
        x: 9 + offset,
        y: 10 + offset,
        width: 45,
        height: 14,
        fill: '#1B1D18',
        stroke: 'transparent',
        text: 'Text',
      }
    case 'ellipse':
      return {
        ...baseElement(type, 'Ellipse'),
        x: 18 + offset,
        y: 20 + offset,
        width: 24,
        height: 24,
        strokeWidth: 0.5,
      }
    case 'line':
      return {
        ...baseElement(type, 'Line'),
        x: 10 + offset,
        y: 30 + offset,
        width: 40,
        height: 0,
        fill: 'transparent',
        stroke: '#1B1D18',
        strokeWidth: 0.8,
      }
    case 'polygon':
      return {
        ...baseElement(type, 'Polygon'),
        x: 17 + offset,
        y: 18 + offset,
        width: 30,
        height: 30,
        strokeWidth: 0.5,
      }
    case 'star':
      return {
        ...baseElement(type, 'Star'),
        x: 17 + offset,
        y: 18 + offset,
        width: 30,
        height: 30,
        strokeWidth: 0.5,
      }
    case 'arc':
      return {
        ...baseElement(type, 'Arc'),
        x: 14 + offset,
        y: 22 + offset,
        width: 36,
        height: 28,
        fillMode: 'none',
        fill: 'transparent',
        strokeWidth: 1.4,
      }
    case 'ring':
      return {
        ...baseElement(type, 'Ring'),
        x: 17 + offset,
        y: 18 + offset,
        width: 30,
        height: 30,
        arcStartAngle: -90,
        arcSweepAngle: 360,
        strokeWidth: 0.5,
      }
    default:
      return {
        ...baseElement(type, 'Rectangle'),
        x: 12 + offset,
        y: 18 + offset,
        width: 39,
        height: 26,
        strokeWidth: 0.5,
      }
  }
}

export const createImageElement = (assetId: string, name = 'Image frame'): VisualElement => ({
  ...createElement('rectangle'),
  name,
  x: 8,
  y: 20,
  width: 47.5,
  height: 42,
  fillMode: 'image',
  fill: '#D8D1C4',
  stroke: '#9E9584',
  strokeWidth: 0.4,
  imageAssetId: assetId,
})

const svgAsset = (id: string, name: string, svg: string): ProjectAsset => ({
  id,
  name,
  mimeType: 'image/svg+xml',
  dataUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
})

const createCuteStarterAssets = () => {
  const firstId = newId('asset')
  const secondId = newId('asset')
  const thirdId = newId('asset')
  const ember = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="420" viewBox="0 0 600 420"><defs><linearGradient id="g" x2="1" y2="1"><stop stop-color="#17231f"/><stop offset="1" stop-color="#3c251e"/></linearGradient></defs><rect width="600" height="420" fill="url(#g)"/><circle cx="470" cy="86" r="145" fill="#ed6b3b" opacity=".22"/><path d="M196 137 253 69l47 77 47-77 58 68-19 153-86 63-87-63z" fill="#e96b3e"/><path d="m219 125 31-35 9 61zm162 0-31-35-9 61z" fill="#f3b56d"/><path d="M252 237q48 46 96 0-10 78-48 78t-48-78" fill="#f5d6a1"/><circle cx="262" cy="198" r="11" fill="#17231f"/><circle cx="338" cy="198" r="11" fill="#17231f"/><path d="m286 235 14-11 14 11-14 12z" fill="#17231f"/><path d="M40 350q95-98 190 0M371 350q94-98 189 0" fill="none" stroke="#f0a15d" stroke-width="15" opacity=".32"/></svg>`
  const moss = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="420" viewBox="0 0 600 420"><defs><linearGradient id="g" x2="1" y2="1"><stop stop-color="#15251f"/><stop offset="1" stop-color="#47613d"/></linearGradient></defs><rect width="600" height="420" fill="url(#g)"/><circle cx="300" cy="205" r="126" fill="#d6c58f" opacity=".9"/><circle cx="300" cy="205" r="92" fill="#294638"/><path d="M300 300V139M300 207q-92-85-165-14M300 239q91-87 169-21" fill="none" stroke="#b8cc83" stroke-width="18" stroke-linecap="round"/><g fill="#8eaa63"><ellipse cx="157" cy="177" rx="61" ry="29" transform="rotate(26 157 177)"/><ellipse cx="199" cy="238" rx="53" ry="25" transform="rotate(-22 199 238)"/><ellipse cx="439" cy="195" rx="61" ry="29" transform="rotate(-27 439 195)"/><ellipse cx="400" cy="253" rx="52" ry="24" transform="rotate(23 400 253)"/></g><g fill="#dce4a6"><circle cx="81" cy="73" r="13"/><circle cx="519" cy="91" r="18"/><circle cx="92" cy="340" r="20"/><circle cx="524" cy="331" r="12"/></g></svg>`
  const tide = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="420" viewBox="0 0 600 420"><defs><linearGradient id="g" x2="1" y2="1"><stop stop-color="#101f34"/><stop offset="1" stop-color="#315f78"/></linearGradient></defs><rect width="600" height="420" fill="url(#g)"/><circle cx="427" cy="103" r="78" fill="#e9dfba" opacity=".88"/><circle cx="457" cy="82" r="78" fill="#21455e"/><path d="M-20 286q75-75 150 0t150 0 150 0 150 0 150 0v154H-20z" fill="#4c90aa"/><path d="M-20 327q75-75 150 0t150 0 150 0 150 0 150 0v113H-20z" fill="#87bdc6"/><path d="M-20 365q75-75 150 0t150 0 150 0 150 0 150 0v75H-20z" fill="#d1e2d8"/><path d="M132 256q76-174 174 0-92-63-174 0" fill="#e4d7ae" opacity=".72"/><circle cx="82" cy="91" r="8" fill="#d1e2d8"/><circle cx="127" cy="130" r="15" fill="#d1e2d8" opacity=".65"/></svg>`
  return {
    ids: { firstId, secondId, thirdId },
    assets: {
      [firstId]: svgAsset(firstId, 'Ember Fox artwork.svg', ember),
      [secondId]: svgAsset(secondId, 'Moss Keeper artwork.svg', moss),
      [thirdId]: svgAsset(thirdId, 'Tide Caller artwork.svg', tide),
    },
  }
}

const createSpookyStarterAssets = () => {
  const firstId = newId('asset')
  const secondId = newId('asset')
  const thirdId = newId('asset')
  const lantern = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="420" viewBox="0 0 600 420"><defs><radialGradient id="glow"><stop stop-color="#ffcf65"/><stop offset=".45" stop-color="#f27a24"/><stop offset="1" stop-color="#5f1b28"/></radialGradient><linearGradient id="night" x2="1" y2="1"><stop stop-color="#100b1a"/><stop offset="1" stop-color="#32142b"/></linearGradient></defs><rect width="600" height="420" fill="url(#night)"/><circle cx="300" cy="219" r="153" fill="#f27a24" opacity=".12"/><path d="M267 52h66l18 49h-102zM238 122h124l22 220H216z" fill="#121018" stroke="#a34e34" stroke-width="11" stroke-linejoin="round"/><path d="M257 151h86l12 154h-110z" fill="url(#glow)"/><path d="m269 212 22-20 11 23 19-22 17 20-18 2-15 24-16-23zM273 261q27 31 58 0-8 46-30 46t-28-46" fill="#18101b"/><path d="M209 341h182M272 52q28-45 56 0" fill="none" stroke="#d16b3c" stroke-width="10" stroke-linecap="round"/><g fill="#9d5cd0"><path d="M90 95q35-28 70 0-35-7-70 0"/><path d="M443 78q34-28 68 0-34-7-68 0"/><path d="M66 285q31-25 62 0-31-6-62 0"/></g></svg>`
  const fang = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="420" viewBox="0 0 600 420"><defs><linearGradient id="night" x2="1" y2="1"><stop stop-color="#0d0a16"/><stop offset="1" stop-color="#29103c"/></linearGradient><radialGradient id="moon"><stop stop-color="#e7c7ff"/><stop offset="1" stop-color="#8a43bd"/></radialGradient></defs><rect width="600" height="420" fill="url(#night)"/><circle cx="430" cy="113" r="88" fill="url(#moon)" opacity=".9"/><circle cx="463" cy="92" r="88" fill="#1e1230"/><path d="M300 137q-67-82-187-37 54 18 64 62-49 9-76 55 82-19 139 39l60 78 60-78q57-58 139-39-27-46-76-55 10-44 64-62-120-45-187 37z" fill="#17101f" stroke="#a855f7" stroke-width="10" stroke-linejoin="round"/><path d="m269 181 31 35 31-35-31 100z" fill="#d8b4fe"/><circle cx="258" cy="183" r="10" fill="#e06baf"/><circle cx="342" cy="183" r="10" fill="#e06baf"/><path d="M107 343 82 318m411 25 25-25M85 70 58 44m477 26 27-26" stroke="#a855f7" stroke-width="8" stroke-linecap="round" opacity=".55"/></svg>`
  const choir = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="420" viewBox="0 0 600 420"><defs><linearGradient id="night" x2="1" y2="1"><stop stop-color="#081510"/><stop offset="1" stop-color="#172b29"/></linearGradient><linearGradient id="spirit" x2="0" y2="1"><stop stop-color="#d9ffd6"/><stop offset="1" stop-color="#55bd76" stop-opacity=".18"/></linearGradient></defs><rect width="600" height="420" fill="url(#night)"/><path d="M110 380q-25-111 21-203 43-86 106-20 63-99 126 0 63-66 106 20 46 92 21 203-52-42-94 0-48-48-96 0-48-48-96 0-42-42-94 0z" fill="url(#spirit)" stroke="#71d96b" stroke-width="8"/><g fill="#10201b"><ellipse cx="199" cy="214" rx="16" ry="24"/><ellipse cx="300" cy="174" rx="16" ry="24"/><ellipse cx="401" cy="214" rx="16" ry="24"/><path d="M174 270q25-31 50 0-25-10-50 0M275 235q25-31 50 0-25-10-50 0M376 270q25-31 50 0-25-10-50 0"/></g><g fill="#71d96b" opacity=".5"><circle cx="78" cy="94" r="8"/><circle cx="118" cy="64" r="13"/><circle cx="506" cy="105" r="12"/><circle cx="538" cy="72" r="7"/></g><path d="M63 340q55-78 109 0m256 0q55-78 109 0" fill="none" stroke="#71d96b" stroke-width="9" opacity=".28"/></svg>`
  return {
    ids: { firstId, secondId, thirdId },
    assets: {
      [firstId]: svgAsset(firstId, 'Gloom Lantern artwork.svg', lantern),
      [secondId]: svgAsset(secondId, 'Velvet Fang artwork.svg', fang),
      [thirdId]: svgAsset(thirdId, 'Hollow Choir artwork.svg', choir),
    },
  }
}

interface StarterVariableIds {
  titleId: string
  typeId: string
  bodyId: string
  powerId: string
  accentId: string
  artId: string
}

interface StarterBox {
  x: number
  y: number
  width: number
  height: number
}

interface StarterTextBox extends StarterBox {
  fontSize: number
  lineHeight?: number
}

interface StarterLayout {
  detailScale: number
  borderWidthMm: number
  borderInnerRadiusMm: number
  artwork: StarterBox
  accent: StarterBox
  rulesPanel: StarterBox
  type: StarterTextBox
  title: StarterTextBox
  powerBadge: StarterBox
  power: StarterTextBox
  rulesLabel: StarterTextBox
  body: StarterTextBox
}

interface SpookyStarterLayout extends StarterLayout {
  titleVeil: StarterBox
}

const STARTER_LAYOUTS: Record<string, StarterLayout> = {
  'poker-bgm': {
    detailScale: 1,
    borderWidthMm: 2.2,
    borderInnerRadiusMm: 2,
    artwork: { x: 3.5, y: 3.5, width: 56.5, height: 40 },
    accent: { x: 4, y: 44.5, width: 55.5, height: 2.2 },
    rulesPanel: { x: 4, y: 61, width: 55.5, height: 23.5 },
    type: { x: 5, y: 48, width: 40, height: 3.5, fontSize: 2.5 },
    title: { x: 5, y: 51.7, width: 42, height: 7.2, fontSize: 5.5 },
    powerBadge: { x: 48.5, y: 47.5, width: 10.5, height: 10.5 },
    power: { x: 48.5, y: 49.5, width: 10.5, height: 6, fontSize: 5.2 },
    rulesLabel: { x: 7, y: 63, width: 49.5, height: 3, fontSize: 2.2 },
    body: { x: 7, y: 66.5, width: 49.5, height: 15, fontSize: 3.1 },
  },
  bridge: {
    detailScale: 0.9,
    borderWidthMm: 2,
    borderInnerRadiusMm: 1.8,
    artwork: { x: 3.5, y: 3.5, width: 50.15, height: 37.5 },
    accent: { x: 3.8, y: 42, width: 49.55, height: 2.2 },
    rulesPanel: { x: 3.8, y: 60.5, width: 49.55, height: 24.2 },
    type: { x: 4.5, y: 45.5, width: 35, height: 3.3, fontSize: 2.3 },
    title: { x: 4.5, y: 49, width: 37.5, height: 7, fontSize: 4.8 },
    powerBadge: { x: 43.8, y: 46.8, width: 9.3, height: 9.3 },
    power: { x: 43.8, y: 48.5, width: 9.3, height: 5.5, fontSize: 4.5 },
    rulesLabel: { x: 5.8, y: 62.5, width: 45.3, height: 2.8, fontSize: 2 },
    body: { x: 5.8, y: 65.8, width: 45.3, height: 15.6, fontSize: 2.8 },
  },
  tarot: {
    detailScale: 1.1,
    borderWidthMm: 2.4,
    borderInnerRadiusMm: 2.2,
    artwork: { x: 3.8, y: 3.8, width: 62.25, height: 55 },
    accent: { x: 4.2, y: 60.2, width: 61.45, height: 2.5 },
    rulesPanel: { x: 4.2, y: 80.5, width: 61.45, height: 35.7 },
    type: { x: 5.2, y: 64.2, width: 45, height: 3.8, fontSize: 2.7 },
    title: { x: 5.2, y: 68.7, width: 48, height: 8, fontSize: 6 },
    powerBadge: { x: 55, y: 64, width: 10.5, height: 10.5 },
    power: { x: 55, y: 66, width: 10.5, height: 6.5, fontSize: 5.4 },
    rulesLabel: { x: 7, y: 83, width: 55, height: 3, fontSize: 2.3 },
    body: { x: 7, y: 87, width: 55, height: 24, fontSize: 3.4 },
  },
  mini: {
    detailScale: 0.75,
    borderWidthMm: 1.6,
    borderInnerRadiusMm: 1.4,
    artwork: { x: 3.5, y: 3.5, width: 37.45, height: 24.2 },
    accent: { x: 3.8, y: 28.7, width: 36.85, height: 1.6 },
    rulesPanel: { x: 3.8, y: 40.5, width: 36.85, height: 19 },
    type: { x: 4.2, y: 31.5, width: 25, height: 2.2, fontSize: 1.7 },
    title: { x: 4.2, y: 34.1, width: 27, height: 4.8, fontSize: 3.4 },
    powerBadge: { x: 32, y: 31.2, width: 8.3, height: 8.3 },
    power: { x: 32, y: 32.8, width: 8.3, height: 4.8, fontSize: 3.7 },
    rulesLabel: { x: 5.2, y: 42.3, width: 34, height: 2, fontSize: 1.5 },
    body: { x: 5.2, y: 44.8, width: 34, height: 12.3, fontSize: 2, lineHeight: 1.15 },
  },
  square: {
    detailScale: 0.9,
    borderWidthMm: 2.2,
    borderInnerRadiusMm: 2,
    artwork: { x: 3.5, y: 3.5, width: 63, height: 31.5 },
    accent: { x: 3.8, y: 36.2, width: 62.4, height: 2 },
    rulesPanel: { x: 3.8, y: 50.5, width: 62.4, height: 15.7 },
    type: { x: 5, y: 39.4, width: 43, height: 2.4, fontSize: 1.8 },
    title: { x: 5, y: 42.1, width: 48, height: 6.2, fontSize: 4.6 },
    powerBadge: { x: 56, y: 39.3, width: 9.5, height: 9.5 },
    power: { x: 56, y: 41, width: 9.5, height: 5.2, fontSize: 4.3 },
    rulesLabel: { x: 6, y: 52.2, width: 58, height: 2.1, fontSize: 1.7 },
    body: { x: 6, y: 54.8, width: 58, height: 9.2, fontSize: 2.4, lineHeight: 1.15 },
  },
}

const SPOOKY_STARTER_LAYOUTS: Record<string, SpookyStarterLayout> = {
  'poker-bgm': {
    detailScale: 1,
    borderWidthMm: 1.6,
    borderInnerRadiusMm: 0.8,
    artwork: { x: 2.8, y: 2.8, width: 57.9, height: 83.3 },
    titleVeil: { x: 3.4, y: 3.4, width: 45.8, height: 17.5 },
    accent: { x: 4.4, y: 5.4, width: 1.2, height: 12 },
    rulesPanel: { x: 3.8, y: 57.5, width: 55.9, height: 27.3 },
    type: { x: 7, y: 5.4, width: 38, height: 3.2, fontSize: 2.1 },
    title: { x: 7, y: 8.8, width: 40.5, height: 8.5, fontSize: 3.7 },
    powerBadge: { x: 50.5, y: 5.5, width: 8.5, height: 8.5 },
    power: { x: 50.5, y: 7, width: 8.5, height: 5, fontSize: 4 },
    rulesLabel: { x: 6.2, y: 60.2, width: 49, height: 2.8, fontSize: 1.9 },
    body: { x: 6.2, y: 63.5, width: 49, height: 18, fontSize: 2.7 },
  },
  bridge: {
    detailScale: 0.9,
    borderWidthMm: 1.5,
    borderInnerRadiusMm: 0.7,
    artwork: { x: 2.6, y: 2.6, width: 51.95, height: 83.7 },
    titleVeil: { x: 3.2, y: 3.2, width: 41.7, height: 16.5 },
    accent: { x: 4.2, y: 5.2, width: 1.1, height: 11.5 },
    rulesPanel: { x: 3.6, y: 57, width: 49.95, height: 28 },
    type: { x: 6.4, y: 5.2, width: 32, height: 3, fontSize: 1.8 },
    title: { x: 6.4, y: 8.4, width: 36.5, height: 8, fontSize: 3.2 },
    powerBadge: { x: 46, y: 5.2, width: 7.6, height: 7.6 },
    power: { x: 46, y: 6.5, width: 7.6, height: 4.5, fontSize: 3.5 },
    rulesLabel: { x: 5.8, y: 59.6, width: 43, height: 2.7, fontSize: 1.7 },
    body: { x: 5.8, y: 62.8, width: 43, height: 18.5, fontSize: 2.5 },
  },
  tarot: {
    detailScale: 1.1,
    borderWidthMm: 1.8,
    borderInnerRadiusMm: 0.9,
    artwork: { x: 3, y: 3, width: 63.85, height: 114.65 },
    titleVeil: { x: 3.8, y: 3.8, width: 51.5, height: 19.5 },
    accent: { x: 5, y: 6.2, width: 1.4, height: 14 },
    rulesPanel: { x: 4.2, y: 75, width: 61.45, height: 40.8 },
    type: { x: 8, y: 6.2, width: 40, height: 3.5, fontSize: 2.2 },
    title: { x: 8, y: 10.1, width: 45, height: 9.5, fontSize: 4 },
    powerBadge: { x: 56.5, y: 6, width: 8.7, height: 8.7 },
    power: { x: 56.5, y: 7.5, width: 8.7, height: 5.2, fontSize: 4.1 },
    rulesLabel: { x: 7, y: 78, width: 55, height: 3.2, fontSize: 2.1 },
    body: { x: 7, y: 82, width: 55, height: 28.5, fontSize: 3.1 },
  },
  mini: {
    detailScale: 0.75,
    borderWidthMm: 1.2,
    borderInnerRadiusMm: 0.6,
    artwork: { x: 2.2, y: 2.2, width: 40.05, height: 59.1 },
    titleVeil: { x: 2.7, y: 2.7, width: 30.8, height: 12 },
    accent: { x: 3.5, y: 4.2, width: 0.9, height: 8 },
    rulesPanel: { x: 3, y: 39.8, width: 38.45, height: 20.6 },
    type: { x: 5.2, y: 4.2, width: 22, height: 2.2, fontSize: 1.3 },
    title: { x: 5.2, y: 6.6, width: 26.2, height: 5.5, fontSize: 2.25 },
    powerBadge: { x: 34.5, y: 4.4, width: 6.2, height: 6.2 },
    power: { x: 34.5, y: 5.5, width: 6.2, height: 3.7, fontSize: 2.8 },
    rulesLabel: { x: 5, y: 41.8, width: 34, height: 2, fontSize: 1.3 },
    body: { x: 5, y: 44.2, width: 34, height: 13.5, fontSize: 1.75, lineHeight: 1.15 },
  },
  square: {
    detailScale: 0.9,
    borderWidthMm: 1.6,
    borderInnerRadiusMm: 0.8,
    artwork: { x: 2.8, y: 2.8, width: 64.4, height: 64.4 },
    titleVeil: { x: 3.5, y: 3.5, width: 49.5, height: 18 },
    accent: { x: 5, y: 6, width: 1.2, height: 13 },
    rulesPanel: { x: 3.8, y: 43.2, width: 62.4, height: 23 },
    type: { x: 8, y: 6.3, width: 38, height: 2.5, fontSize: 1.5 },
    title: { x: 8, y: 9.2, width: 42, height: 7, fontSize: 3.3 },
    powerBadge: { x: 56, y: 5.5, width: 9.5, height: 9.5 },
    power: { x: 56, y: 7.2, width: 9.5, height: 5.3, fontSize: 4.2 },
    rulesLabel: { x: 6.3, y: 45.5, width: 56, height: 2.4, fontSize: 1.6 },
    body: { x: 6.3, y: 48.4, width: 56, height: 15, fontSize: 2.2, lineHeight: 1.15 },
  },
}

const starterLayoutFor = (presetId: string) => STARTER_LAYOUTS[presetId] ?? STARTER_LAYOUTS['poker-bgm']
const spookyStarterLayoutFor = (presetId: string) => SPOOKY_STARTER_LAYOUTS[presetId] ?? SPOOKY_STARTER_LAYOUTS['poker-bgm']

const createCuteStarterElements = ({ titleId, typeId, bodyId, powerId, accentId, artId }: StarterVariableIds, defaultArtId: string, presetId: string) => {
  const layout = starterLayoutFor(presetId)
  const artwork: VisualElement = {
    ...createElement('rectangle'),
    name: 'Card artwork',
    ...layout.artwork,
    fillMode: 'image',
    fill: '#27322D',
    stroke: '#171A17',
    strokeWidth: 0.6 * layout.detailScale,
    cornerRadius: 2.3 * layout.detailScale,
    imageAssetId: defaultArtId,
    bindings: { imageAssetId: artId },
  }
  const accent: VisualElement = {
    ...createElement('rectangle'),
    name: 'Accent bar',
    ...layout.accent,
    fill: '#E66B3C',
    stroke: 'transparent',
    strokeWidth: 0,
    cornerRadius: layout.accent.height / 2,
    bindings: { fill: accentId },
  }
  const rulesPanel: VisualElement = {
    ...createElement('rectangle'),
    name: 'Rules panel',
    ...layout.rulesPanel,
    fill: '#FBF8F1',
    stroke: '#C9BEAC',
    strokeWidth: 0.35 * layout.detailScale,
    cornerRadius: 2 * layout.detailScale,
    shadow: { ...createDefaultShadow(), enabled: true, opacity: 0.16, offsetX: 0.7 * layout.detailScale, offsetY: 0.8 * layout.detailScale, blur: 1.2 * layout.detailScale },
  }
  const type: VisualElement = {
    ...createElement('text'),
    name: 'Card type',
    ...layout.type,
    text: 'BEAST',
    fill: '#66665C',
    fontSize: layout.type.fontSize,
    bindings: { text: typeId },
  }
  const title: VisualElement = {
    ...createElement('text'),
    name: 'Card title',
    ...layout.title,
    text: 'EMBER FOX',
    fill: '#1C1E1A',
    fontSize: layout.title.fontSize,
    letterSpacing: 0.08,
    bindings: { text: titleId },
  }
  const powerBadgeBase = createElement('ellipse')
  const powerBadge: VisualElement = {
    ...powerBadgeBase,
    name: 'Power badge',
    ...layout.powerBadge,
    fillMode: 'gradient',
    fill: '#E66B3C',
    gradient: {
      ...powerBadgeBase.gradient,
      type: 'radial',
      centerX: 0.32,
      centerY: 0.28,
      radius: 0.72,
      stops: [
        { ...powerBadgeBase.gradient.stops[0], color: '#E66B3C' },
        { ...powerBadgeBase.gradient.stops[1], color: '#A9422C' },
      ],
    },
    shadow: { ...createDefaultShadow(), enabled: true, opacity: 0.3, offsetX: 0.8 * layout.detailScale, offsetY: 0.9 * layout.detailScale, blur: layout.detailScale },
    stroke: '#171A17',
    strokeWidth: 0.45 * layout.detailScale,
    bindings: { [gradientStopBindingKey(powerBadgeBase.gradient.stops[0].id, 'color')]: accentId },
  }
  const power: VisualElement = {
    ...createElement('text'),
    name: 'Power value',
    ...layout.power,
    text: '3',
    fill: '#FFF9EC',
    fontSize: layout.power.fontSize,
    align: 'center',
    verticalAlign: 'middle',
    bindings: { text: powerId },
  }
  const rulesLabel: VisualElement = {
    ...createElement('text'),
    name: 'Rules label',
    ...layout.rulesLabel,
    text: 'ABILITY',
    fill: '#E66B3C',
    fontSize: layout.rulesLabel.fontSize,
    bindings: { fill: accentId },
  }
  const body: VisualElement = {
    ...createElement('text'),
    name: 'Rules text',
    ...layout.body,
    text: 'Move through one occupied space. Draw a card if you end beside a flame.',
    fill: '#45483F',
    fontSize: layout.body.fontSize,
    lineHeight: layout.body.lineHeight ?? 1.22,
    fontWeight: 400,
    bindings: { text: bodyId },
  }
  return [artwork, accent, rulesPanel, type, title, powerBadge, power, rulesLabel, body]
}

const createSpookyStarterElements = ({ titleId, typeId, bodyId, powerId, accentId, artId }: StarterVariableIds, defaultArtId: string, presetId: string) => {
  const layout = spookyStarterLayoutFor(presetId)
  const artwork: VisualElement = {
    ...createElement('rectangle'),
    name: 'Omen artwork',
    ...layout.artwork,
    fillMode: 'image',
    fill: '#100C18',
    stroke: '#F27A24',
    strokeWidth: 0.8 * layout.detailScale,
    cornerRadius: 0.8 * layout.detailScale,
    imageAssetId: defaultArtId,
    shadow: { ...createDefaultShadow(), enabled: true, color: '#050307', opacity: 0.55, offsetX: 0.8 * layout.detailScale, offsetY: 1 * layout.detailScale, blur: 1.4 * layout.detailScale },
    bindings: { imageAssetId: artId, stroke: accentId },
  }
  const titleVeil: VisualElement = {
    ...createElement('rectangle'),
    name: 'Title veil',
    ...layout.titleVeil,
    fill: '#0F0B16',
    stroke: '#F27A24',
    strokeWidth: 0.25 * layout.detailScale,
    cornerRadius: 0,
    opacity: 0.9,
    bindings: { stroke: accentId },
  }
  const accent: VisualElement = {
    ...createElement('rectangle'),
    name: 'Signal slash',
    ...layout.accent,
    fill: '#F27A24',
    stroke: 'transparent',
    strokeWidth: 0,
    cornerRadius: 0,
    bindings: { fill: accentId },
  }
  const rulesPanel: VisualElement = {
    ...createElement('rectangle'),
    name: 'Dark rules panel',
    ...layout.rulesPanel,
    fill: '#17131F',
    stroke: '#5C4866',
    strokeWidth: 0.45 * layout.detailScale,
    strokeStyle: 'dashed',
    strokeDashLength: 1.6 * layout.detailScale,
    strokeDashGap: 0.9 * layout.detailScale,
    cornerRadius: 0.8 * layout.detailScale,
    shadow: { ...createDefaultShadow(), enabled: true, color: '#050307', opacity: 0.52, offsetX: 0.8 * layout.detailScale, offsetY: 1 * layout.detailScale, blur: 1.2 * layout.detailScale },
  }
  const type: VisualElement = {
    ...createElement('text'),
    name: 'Omen type',
    ...layout.type,
    text: 'RELIC',
    fill: '#F27A24',
    fontSize: layout.type.fontSize,
    letterSpacing: 0.18 * layout.detailScale,
    bindings: { text: typeId, fill: accentId },
  }
  const title: VisualElement = {
    ...createElement('text'),
    name: 'Omen title',
    ...layout.title,
    text: 'GLOOM LANTERN',
    fill: '#F6EDDF',
    fontFamily: 'Georgia',
    fontSize: layout.title.fontSize,
    letterSpacing: 0.05 * layout.detailScale,
    bindings: { text: titleId },
  }
  const powerBadgeBase = createElement('polygon')
  const powerBadge: VisualElement = {
    ...powerBadgeBase,
    name: 'Hex power seal',
    ...layout.powerBadge,
    polygonSides: 6,
    fillMode: 'gradient',
    fill: '#F27A24',
    gradient: {
      ...powerBadgeBase.gradient,
      type: 'radial',
      centerX: 0.35,
      centerY: 0.3,
      radius: 0.75,
      stops: [
        { ...powerBadgeBase.gradient.stops[0], color: '#F27A24' },
        { ...powerBadgeBase.gradient.stops[1], color: '#4B1730' },
      ],
    },
    shadow: { ...createDefaultShadow(), enabled: true, color: '#050307', opacity: 0.7, offsetX: 0.8 * layout.detailScale, offsetY: 1 * layout.detailScale, blur: 1.1 * layout.detailScale },
    stroke: '#F6EDDF',
    strokeWidth: 0.45 * layout.detailScale,
    bindings: { [gradientStopBindingKey(powerBadgeBase.gradient.stops[0].id, 'color')]: accentId },
  }
  const power: VisualElement = {
    ...createElement('text'),
    name: 'Power value',
    ...layout.power,
    text: '3',
    fill: '#FFF9EC',
    fontFamily: 'Georgia',
    fontSize: layout.power.fontSize,
    align: 'center',
    verticalAlign: 'middle',
    bindings: { text: powerId },
  }
  const rulesLabel: VisualElement = {
    ...createElement('text'),
    name: 'Whisper label',
    ...layout.rulesLabel,
    text: 'WHISPER',
    fill: '#F27A24',
    fontSize: layout.rulesLabel.fontSize,
    letterSpacing: 0.12 * layout.detailScale,
    bindings: { fill: accentId },
  }
  const body: VisualElement = {
    ...createElement('text'),
    name: 'Omen text',
    ...layout.body,
    text: 'Reveal the top card. If its power is odd, keep it and dim one nearby light.',
    fill: '#D8CFDD',
    fontSize: layout.body.fontSize,
    lineHeight: layout.body.lineHeight ?? 1.22,
    fontWeight: 400,
    bindings: { text: bodyId },
  }
  return [artwork, titleVeil, accent, rulesPanel, type, title, powerBadge, power, rulesLabel, body]
}

const validCustomDimension = (value: number | undefined, fallback: number) => (
  typeof value === 'number' && Number.isFinite(value) && value >= 10 ? value : fallback
)

export const createDeckProject = (
  name: string,
  presetId = CARD_PRESETS[0].id,
  starterId: StarterProjectId = 'cute',
  includeBleed = true,
  customSize?: CustomCardSize,
): DeckProject => {
  const now = new Date().toISOString()
  const isCustom = presetId === CUSTOM_CARD_PRESET.id
  const matchedPreset = CARD_PRESETS.find((candidate) => candidate.id === presetId) ?? CARD_PRESETS[0]
  const requestedWidthMm = customSize?.widthMm
  const requestedHeightMm = customSize?.heightMm
  const preset = isCustom
    ? {
        ...CUSTOM_CARD_PRESET,
        widthMm: validCustomDimension(requestedWidthMm, CUSTOM_CARD_PRESET.widthMm),
        heightMm: validCustomDimension(requestedHeightMm, CUSTOM_CARD_PRESET.heightMm),
      }
    : matchedPreset
  const withStarter = starterId !== 'empty' && !isCustom
  const withSpookyStarter = starterId === 'spooky' && !isCustom
  const starterLayout = withSpookyStarter ? spookyStarterLayoutFor(preset.id) : starterLayoutFor(preset.id)
  const titleId = newId('variable')
  const typeId = newId('variable')
  const bodyId = newId('variable')
  const powerId = newId('variable')
  const accentId = newId('variable')
  const artId = newId('variable')
  const starterAssets = withSpookyStarter ? createSpookyStarterAssets() : withStarter ? createCuteStarterAssets() : null
  const starterCards = withSpookyStarter
    ? [
        {
          name: 'Gloom Lantern',
          title: 'GLOOM LANTERN',
          type: 'RELIC',
          body: 'Reveal the top card. If its power is odd, keep it and dim one nearby light.',
          power: 3,
          accent: '#F27A24',
          artwork: starterAssets!.ids.firstId,
        },
        {
          name: 'Velvet Fang',
          title: 'VELVET FANG',
          type: 'CREATURE',
          body: 'Move through one shadow. Gain one power if no rival can see your path.',
          power: 4,
          accent: '#A855F7',
          artwork: starterAssets!.ids.secondId,
        },
        {
          name: 'Hollow Choir',
          title: 'HOLLOW CHOIR',
          type: 'SPIRIT',
          body: 'Return one discarded card to your hand. It loses one power until dawn.',
          power: 6,
          accent: '#71D96B',
          artwork: starterAssets!.ids.thirdId,
        },
      ]
    : withStarter
      ? [
          {
            name: 'Ember Fox',
            title: 'EMBER FOX',
            type: 'BEAST',
            body: 'Move through one occupied space. Draw a card if you end beside a flame.',
            power: 3,
            accent: '#E66B3C',
            artwork: starterAssets!.ids.firstId,
          },
          {
            name: 'Moss Keeper',
            title: 'MOSS KEEPER',
            type: 'GUARDIAN',
            body: 'Protect an adjacent ally. Restore one growth at the end of the round.',
            power: 5,
            accent: '#668B5A',
            artwork: starterAssets!.ids.secondId,
          },
          {
            name: 'Tide Caller',
            title: 'TIDE CALLER',
            type: 'MYSTIC',
            body: 'Shift any token one current. You may follow it without spending movement.',
            power: 4,
            accent: '#4D83A6',
            artwork: starterAssets!.ids.thirdId,
          },
        ]
      : []
  const variables = withStarter
    ? [
        { id: titleId, name: 'Title', type: 'text' as const, defaultValue: 'Untitled card', columnWidthPx: 180 },
        { id: typeId, name: 'Type', type: 'text' as const, defaultValue: 'CARD', columnWidthPx: 130 },
        { id: bodyId, name: 'Rules', type: 'text' as const, defaultValue: '', columnWidthPx: 280 },
        { id: powerId, name: 'Power', type: 'number' as const, defaultValue: 0, columnWidthPx: defaultVariableWidth('number') },
        { id: accentId, name: 'Accent', type: 'color' as const, defaultValue: starterCards[0].accent, columnWidthPx: defaultVariableWidth('color') },
        { id: artId, name: 'Artwork', type: 'image' as const, defaultValue: starterCards[0].artwork, columnWidthPx: defaultVariableWidth('image') },
      ]
    : []
  const rows = withStarter
    ? starterCards.map((card) => ({
        id: newId('card'),
        name: card.name,
        values: {
          [titleId]: card.title,
          [typeId]: card.type,
          [bodyId]: card.body,
          [powerId]: card.power,
          [accentId]: card.accent,
          [artId]: card.artwork,
        },
      }))
    : [{ id: newId('card'), name: 'Card 1', values: {} }]

  const backgroundGradient = createDefaultGradient('card-background-gradient-stop')
  backgroundGradient.angle = withSpookyStarter ? 135 : 90
  backgroundGradient.stops[0].color = withSpookyStarter ? '#120D1D' : '#F8F1E3'
  backgroundGradient.stops[1].color = withSpookyStarter ? '#32152B' : '#D8E7DF'

  return {
    schemaVersion: 4,
    id: newId('project'),
    name: name.trim() || 'Untitled deck',
    createdAt: now,
    updatedAt: now,
    card: {
      preset: preset.id,
      widthMm: preset.widthMm,
      heightMm: preset.heightMm,
      bleedMm: includeBleed ? preset.recommendedBleedMm : 0,
      safeMm: preset.safeMm,
      cornerRadiusMm: preset.cornerRadiusMm,
      bleedGuideRounded: true,
      safeGuideRounded: true,
      background: withSpookyStarter ? '#16101F' : '#E8DDCA',
      backgroundFillMode: withStarter ? 'gradient' : 'solid',
      backgroundGradient,
      borderEnabled: withStarter,
      borderColor: withSpookyStarter ? '#08060D' : '#171A17',
      borderWidthMm: starterLayout.borderWidthMm,
      borderInnerRadiusMm: starterLayout.borderInnerRadiusMm,
    },
    variables,
    rows,
    elements: withSpookyStarter
      ? createSpookyStarterElements({ titleId, typeId, bodyId, powerId, accentId, artId }, starterAssets!.ids.firstId, preset.id)
      : withStarter
        ? createCuteStarterElements({ titleId, typeId, bodyId, powerId, accentId, artId }, starterAssets!.ids.firstId, preset.id)
        : [],
    groups: [],
    assets: starterAssets?.assets ?? {},
    fonts: {},
    dataSettings: { cardNameWidthPx: 180 },
    render: {
      profile: 'boardgamesmaker',
      dpi: 300,
      pageSize: 'a4',
      pageWidthMm: 210,
      pageHeightMm: 297,
      orientation: 'portrait',
      marginMm: 10,
      gapMm: 4,
      cropMarks: true,
      autoRotate: true,
    },
  }
}
